import { useCallback, useRef } from 'react';
import {
  getChatArtifact,
  runChatArtifactAction,
  submitChatArtifactSuggestion,
} from '../../api/chatApi';
import {
  artifactActionByKey,
  artifactDisplayCompanyIds,
} from './chatArtifactDisplayModel';
import {
  artifactMessageContent,
  discoveryCompleteMessage,
  newMsg,
  ensureOkResponse,
} from './chatMessageModel';
import {
  isAnswerEnrichmentAction,
  isStrategicEnrichmentAction,
} from '../../helper/chatArtifactActionKinds';
import { offerButtonCostLabel } from './chatEnrichmentOfferModel';
import { acknowledgeActionTurn, artifactTurnDisplayPatch, finishArtifactActionTurn, removeActionFlight } from './chatArtifactActionTurn';

const ARTIFACT_ACTION_MAX_ATTEMPTS = 2;

export function isSuccessfulCompletionStatus(status) {
  return status === 'done' || status === 'completed_with_errors';
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

// A suggestion chip strips its React render key before dispatching, leaving only
// suggestion_key/artifact_action_key. Key-dependent routing below (export, save to
// workspace) compares against action.key, so restore it before anything reads it.
function withNormalizedKey(action, actionKey) {
  if (!action || action.key) return action;
  // handleSendSuggestion routes by artifact_action_key ahead of suggestion_key, so
  // the restored key has to agree with the one the lookup was performed under --
  // the two differ on suggestions that name both.
  const derived = firstPresent(action.artifact_action_key, actionKey, action.suggestion_key);
  return derived ? { ...action, key: derived } : action;
}

function answerActionByKey(message, key) {
  const actions = Array.isArray(message?.artifactAnswerActions)
    ? message.artifactAnswerActions
    : [];
  return actions.find((action) => action?.key === key) || null;
}

// A proposed action runs over exactly what was quoted: the proposal's admitted
// companies when it names them (#933), else the whole list, by omitting company_ids
// (/enrich rejects an empty selection). Never the displayed rows.
function actionScope(action, message, idempotent) {
  if (action.proposed) {
    return Array.isArray(action.proposal_company_ids) && action.proposal_company_ids.length > 0
      ? { companyIds: action.proposal_company_ids }
      : {};
  }
  return { companyIds: idempotent ? [] : artifactDisplayCompanyIds(message) };
}

function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `artifact-action-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function artifactIdFromAction(action) {
  const directId = firstPresent(action?.artifact_id, action?.artifactId);
  if (directId) return directId;

  const path = String(action?.endpoint?.path || '');
  const match = path.match(/\/chat\/artifacts\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function answerActionPayload(action, artifactId, idempotencyKey) {
  const path = String(action?.endpoint?.path || '');
  const replayAction = path.includes('/retry-failed-rows') || path.includes('/refresh-all');
  const payload = { artifactId, idempotencyKey };
  if (replayAction) {
    const outputIdentity = firstPresent(action?.output_identity, action?.outputIdentity);
    if (outputIdentity) payload.outputIdentity = outputIdentity;
    return payload;
  }

  const query = firstPresent(action?.query, action?.question);
  if (query) payload.query = query;
  return payload;
}

async function submitIdempotentActionWithRetry(action, payload) {
  let response;
  for (let attempt = 0; attempt < ARTIFACT_ACTION_MAX_ATTEMPTS; attempt += 1) {
    response = await runChatArtifactAction(action, payload);
    if (response) return response;
  }
  return response;
}

function isTypedSuggestionAction(action) {
  const path = String(action?.endpoint?.path || '');
  return Boolean(
    (action?.suggestion_key || action?.artifact_action_key) &&
      path.includes('/suggestions/') &&
      path.endsWith('/submit')
  );
}

async function submitTypedSuggestionWithRetry(artifactId, action, idempotencyKey) {
  let response;
  for (let attempt = 0; attempt < ARTIFACT_ACTION_MAX_ATTEMPTS; attempt += 1) {
    response = await submitChatArtifactSuggestion(artifactId, action, idempotencyKey);
    if (response) return response;
  }
  return response;
}

// Discovery-turn owner progress (#798) refreshes the list card in place.
export function refreshedArtifactPatch(artifactData, message) {
  const { artifactId: _sameArtifact, ...display } = artifactTurnDisplayPatch(
    artifactData, message, artifactData.artifact?.artifact_id || message.artifactId);
  return {
    ...display,
    content: artifactMessageContent(display.artifactDisplay, discoveryCompleteMessage(display.companies)),
  };
}

export async function refreshInterruptedOwnerJob(job, message) {
  if (job?.job_type !== 'artifact_lead_enrichment' || !job.artifact_id) return {};
  try {
    return refreshedArtifactPatch(ensureOkResponse(await getChatArtifact(job.artifact_id),
      'Unable to refresh owner results.'), message);
  } catch (_refreshError) {
    return {}; // Preserve visible rows and the authoritative job outcome.
  }
}

export function useArtifactActionHandler({
  messages, setMessages, artifactActionInFlight, setArtifactActionInFlight,
  updateAssistantMessage, waitForJob, handleExportCompanies, requestConfirmation,
  refreshCredits, sessionId, activeSession, activeJobs,
}) {
  // Reserve before opening confirmation: a second click cannot create a second
  // command or key. Reservations span copies of a list, but not unrelated lists.
  const reservations = useRef(new Set());
  const currentFlight = useRef(artifactActionInFlight);
  currentFlight.current = artifactActionInFlight;
  return useCallback(async (messageId, actionKey, fallbackAction) => {
    const message = messages.find((item) => item.id === messageId);
    const action = artifactActionByKey(message?.artifactDisplay, actionKey)
      || answerActionByKey(message, actionKey) || withNormalizedKey(fallbackAction, actionKey);
    if (!message || !action || action.enabled === false) return undefined;
    const artifactId = message.artifactId || message.referencedArtifactId
      || message.artifactDisplay?.artifactId || artifactIdFromAction(action);
    const key = action.key || actionKey;
    const reservation = JSON.stringify([sessionId, artifactId, key]);
    const alreadyRunning = () => Object.values(currentFlight.current || {}).some(
      (flight) => flight.artifactId === artifactId && flight.key === key
    );
    if (reservations.current.has(reservation) || alreadyRunning()) return undefined;
    if (key === 'save_to_workspace' && message.artifactWorkspace?.workspace_id) return undefined;
    reservations.current.add(reservation);
    const sameSession = () => !activeSession || activeSession.current === sessionId;
    const patch = (id, value) => { if (sameSession()) updateAssistantMessage(id, value); };
    let targetId = messageId;
    let flight = null;
    let drawn = false;
    let trackedJobId;
    try {
      const generatedPaid = fallbackAction?.action_type && fallbackAction.approval_required === true;
      const answer = isAnswerEnrichmentAction(action);
      const strategic = isStrategicEnrichmentAction(action);
      const typed = isTypedSuggestionAction(action);
      const save = key === 'save_to_workspace';
      const sample = key === 'find_leads_sample' || action.scope === 'sample_preview';
      if (action.requires_confirmation || generatedPaid) {
        const confirmationAction = generatedPaid ? {
          ...action, confirmation_message: fallbackAction.prompt, quote: undefined,
          cost_label: `${fallbackAction.action_type === 'find_owners' ? 'Up to ' : ''}${fallbackAction.credit_cost} credits`,
          credit_cost: fallbackAction.credit_cost, scope_count: fallbackAction.row_count,
        } : action;
        if (!await requestConfirmation?.(confirmationAction) || !sameSession() || alreadyRunning()) return undefined;
      }
      if (action.kind === 'export' || key === 'export_csv') return handleExportCompanies(messageId, action);
      const actionTurn = !save && !sample && action.kind !== 'status' && !(typed && action.starts_job === false);
      const cost = offerButtonCostLabel(action);
      const label = (generatedPaid ? fallbackAction.label : [action.label || 'Enrich saved evidence', cost].filter(Boolean).join(' · '))
        .replace(/\s+/g, ' ').trim().slice(0, 200);
      const idempotencyKey = actionTurn || typed ? createIdempotencyKey() : null;
      let user;
      if (actionTurn) {
        user = newMsg('user', label);
        const assistant = { ...newMsg('assistant', 'Running…'),
          jobStatus: 'pending', referencedArtifactId: artifactId, artifactActionKey: key };
        targetId = assistant.id;
        setMessages((previous) => [...previous, user, assistant]);
        drawn = true;
      } else {
        patch(messageId, { artifactActionError: null, artifactActionNotice: null, artifactActionNoticeSeverity: null });
      }
      flight = { key, artifactId, turnId: targetId };
      setArtifactActionInFlight((previous) => ({ ...previous, [messageId]: flight }));
      const scope = generatedPaid && fallbackAction.action_type === 'find_owners' ? {}
        : actionScope(action, message, answer || strategic || typed);
      const payload = answer ? answerActionPayload(action, artifactId, idempotencyKey)
        : strategic ? { artifactId, idempotencyKey }
          : save ? { artifactId } : { artifactId, ...scope, ...(actionTurn ? { idempotencyKey, actionLabel: label } : {}) };
      const response = typed ? await submitTypedSuggestionWithRetry(artifactId, action, idempotencyKey)
        : actionTurn ? await submitIdempotentActionWithRetry(action, payload)
          : await runChatArtifactAction(action, payload);
      const ack = ensureOkResponse(response, 'Unable to run this artifact action.');
      if (save) {
        patch(messageId, {
          artifactWorkspace: ack.workspace_id ? ack : null,
          artifactActionNotice: ack.workspace_id ? null : 'Saved as a Saved Search.',
          artifactActionNoticeSeverity: 'success',
        });
        return undefined;
      }
      trackedJobId = ack.job_id;
      if (trackedJobId) {
        activeJobs?.current.add(trackedJobId);
        const nextFlight = { ...flight, jobId: trackedJobId };
        flight = nextFlight;
        setArtifactActionInFlight((previous) => ({ ...previous, [messageId]: nextFlight }));
      }
      if (actionTurn) {
        const oldId = targetId;
        const oldUserId = user.id;
        targetId = ack.assistant_message_id || targetId;
        user = { ...user, id: ack.message_id || user.id };
        if (sameSession()) {
          setMessages((previous) => acknowledgeActionTurn(previous, {
            optimisticUserId: oldUserId, optimisticAssistantId: oldId,
            userId: user.id, assistantId: targetId, jobId: ack.job_id,
          }));
          // The offer's copy and table stay intact; its approval is now consumed.
          if (message.jobStatus === 'awaiting_approval' || action.proposed) {
            patch(messageId, { jobStatus: 'cancelled', artifactAnswerActions: message.artifactAnswerActions.map(
              (item) => item.key === key ? { ...item, enabled: false } : item) });
          }
        }
      }
      await finishArtifactActionTurn({
        messageId: targetId, jobId: ack.job_id, artifactId,
        message, initialJob: ack, waitForJob, updateAssistantMessage: patch, refreshCredits,
        inPlaceSample: !actionTurn, leadJob: key === 'lead_finder_contacts' || fallbackAction?.action_type === 'find_owners',
      });
    } catch (error) {
      patch(targetId, {
        ...(drawn ? { content: error?.message || 'Unable to run this artifact action.', jobStatus: 'failed', answerConfirmed: false } : {}),
        artifactActionError: drawn ? null : error?.message || 'Unable to run this artifact action.',
        artifactActionNoticeSeverity: null,
      });
      refreshCredits?.();
    } finally {
      // Settling this polling attempt releases ownership even when the job is
      // still running. A later restore can take over after polling defers.
      reservations.current.delete(reservation);
      if (trackedJobId) activeJobs?.current.delete(trackedJobId);
      if (flight) setArtifactActionInFlight((previous) => removeActionFlight(previous, flight));
    }
    return undefined;
  }, [messages, setMessages, setArtifactActionInFlight, updateAssistantMessage, waitForJob,
    handleExportCompanies, requestConfirmation, refreshCredits, sessionId, activeSession, activeJobs]);
}
